import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'styled-components';
import { useContinuousPlaybackHit } from './events/use-continuous-playback-hit';
import { useOnActive } from './events/use-on-active';
import { useOnDataPartLoaded } from './events/use-on-data-part-loaded';
import { useOnError } from './events/use-on-error';
import { useOnInactive } from './events/use-on-inactive';
import { useOnLoaded } from './events/use-on-load';
import { usePlayerEvent } from './events/use-player-event';
import { useProgressChanged } from './events/use-progress-changed';
import { useRecommendationHit } from './events/use-recommendation-hit';
import { useSegmentChanged } from './events/use-segment-changed';
import { useSegmentSwitched } from './events/use-segment-switched';
import { useSegmentVisibilityChanged } from './events/use-segment-visibility-changed';
import { defaultSegmentHeight } from './Letterbox.constants';
import { useLetterbox } from './use-letterbox';
import { jsx as _jsx } from "react/jsx-runtime";
const cssVariableNameSegmentHeight = '--segment-height';
export const Letterbox = ({
  urn,
  escMediaComposition,
  ilHost,
  language,
  seekPosition,
  autoPlay,
  playerFocus,
  standalone,
  textTrackDefaultLanguage,
  textTrackForcedLanguage,
  textTrackSizeLevel,
  hideSegments,
  hideShareOptions,
  showTitle,
  showContinuousPlayback,
  showPlayIcon,
  recommendations,
  endScreenPlaylist,
  currentDate,
  dateChangeDetection,
  onProgressChanged,
  onSegmentChanged,
  onSegmentSwitched,
  onRecommendationHit,
  onContinuousPlaybackHit,
  onSegmentVisibilityChanged,
  onLoaded,
  onDataPartLoaded,
  onPlay,
  onPause,
  onError,
  suppressTracking,
  calculateTimestampOffsetForEachSegment,
  children,
  onInactive,
  onActive
}) => {
  const [playerIsReady, setPlayerIsReady] = useState(false);
  const {
    id,
    player
  } = useLetterbox({
    urn,
    escMediaComposition,
    ilHost,
    language,
    seekPosition,
    autoPlay,
    playerFocus,
    standalone,
    textTrackSizeLevel,
    hideSegments,
    hideShareOptions,
    showTitle,
    showContinuousPlayback,
    showPlayIcon,
    recommendations,
    endScreenPlaylist,
    textTrackDefaultLanguage,
    textTrackForcedLanguage,
    suppressTracking,
    calculateTimestampOffsetForEachSegment
  });
  useEffect(() => {
    if (playerIsReady && currentDate) {
      player?.currentDate(new Date(currentDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerIsReady, currentDate, dateChangeDetection]);
  useOnDataPartLoaded(player, onDataPartLoaded);
  useOnError(player, onError);
  useProgressChanged(player, onProgressChanged);
  useSegmentChanged(player, onSegmentChanged);
  useSegmentSwitched(player, onSegmentSwitched);
  useSegmentVisibilityChanged(player, onSegmentVisibilityChanged);
  useRecommendationHit(player, onRecommendationHit);
  useContinuousPlaybackHit(player, onContinuousPlaybackHit);
  useOnInactive(player, onInactive);
  useOnActive(player, onActive);
  useOnLoaded(player, () => {
    player && onLoaded && onLoaded(player);
    setPlayerIsReady(true);
  });
  usePlayerEvent(player, 'play', () => player && onPlay && onPlay(player));
  usePlayerEvent(player, 'pause', () => player && onPause && onPause(player));
  const playerRoot = document.getElementById(player?.player.id());
  return /*#__PURE__*/_jsx(StyledLetterbox, {
    id: id,
    style: {
      width: '100%',
      height: '100%',
      [cssVariableNameSegmentHeight]: `${defaultSegmentHeight}px`
    },
    children: playerRoot && /*#__PURE__*/createPortal(/*#__PURE__*/_jsx(PlayerOverlay, {
      children: children
    }), playerRoot)
  });
};
const PlayerOverlay = styled.div.withConfig({
  displayName: "Letterbox__PlayerOverlay",
  componentId: "sc-9pa6cl-0"
})(["font-family:var(--font-family);"]);
const StyledLetterbox = styled.div.withConfig({
  displayName: "Letterbox__StyledLetterbox",
  componentId: "sc-9pa6cl-1"
})([".vjs-srgssr-skin.srgssr-has-subdivisions .vjs-tech{height:calc(100% - var(", "));}.vjs-srgssr-skin.srgssr-has-subdivisions.vjs-fullscreen .vjs-tech{height:100%;}.vjs-srgssr-skin:not(.vjs-fullscreen).srgssr-has-subdivisions .vjs-poster{height:calc(100% - var(", "));}.vjs-srgssr-skin.srgssr-has-subdivisions .vjs-srgssr-overlay{height:calc(100% - var(", "));}.vjs-srgssr-skin.srgssr-has-subdivisions:not(.srgssr-touch-enabled,.vjs-ended.vjs-fullscreen) .srgssr-end-screen{padding-bottom:var(", ");}.vjs-srgssr-skin.srgssr-has-subdivisions .vjs-control-bar{bottom:var(", ");}.vjs-srgssr-skin.srgssr-has-subdivisions.vjs-fullscreen .vjs-control-bar{bottom:0;}.vjs-srgssr-skin .srgssr-subdivisions{height:var(", ");}.vjs-srgssr-skin .srgssr-subdivisions__arrow{height:var(", ");}.vjs-srgssr-skin .subdivision{font-size:10px;}"], cssVariableNameSegmentHeight, cssVariableNameSegmentHeight, cssVariableNameSegmentHeight, cssVariableNameSegmentHeight, cssVariableNameSegmentHeight, cssVariableNameSegmentHeight, cssVariableNameSegmentHeight);